import {classNames} from '@cohort/shared-frontend/utils/classNames';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {SectionName} from '@cohort/wallet/lib/Utils';
import {SCROLL_SPY_CONTAINER_ID} from '@cohort/wallet/lib/Utils';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useInView} from 'react-intersection-observer';
import {useLocation} from 'react-router-dom';
import {Element as ScrollElement, scroller} from 'react-scroll';

type MobileSectionProps = {
  children: React.ReactNode;
  name: SectionName;
  onVisibilityChange: (name: SectionName, isVisible: boolean) => void;
  last?: boolean;
};

export const MobileSection: React.FC<MobileSectionProps> = ({
  children,
  name,
  onVisibilityChange,
  last,
}) => {
  const {hasDarkBg} = useThemeContext();
  const {ref: sectionRef, inView: sectionRefInView} = useInView();

  const {hash} = useLocation();

  const {t} = useTranslation('components', {keyPrefix: 'mobileSection'});

  useEffect(() => {
    onVisibilityChange(name, sectionRefInView);
  }, [sectionRefInView, name, onVisibilityChange]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (sectionRef !== null && sectionRef !== undefined && hash === `#${name}`) {
      scroller.scrollTo(name, {
        duration: 750,
        smooth: true,
        offset: -50,
        containerId: SCROLL_SPY_CONTAINER_ID,
      });
    }
  }, [hash, name, sectionRef]);

  // i18nOwl-ignore [descriptionSectionTitle, faqSectionTitle, perksSectionTitle]
  return (
    <ScrollElement name={name} className="mt-5">
      <section id={name} ref={sectionRef} className={classNames('scroll-mt-14', last && 'pb-8')}>
        <h3
          className={classNames(
            'border-b px-4 py-5 text-lg font-bold leading-6',
            hasDarkBg ? 'border-white/20' : 'border-black/20'
          )}
        >
          {t(`${name}SectionTitle`)}
        </h3>
        <div className="px-4 py-5">{children}</div>
      </section>
    </ScrollElement>
  );
};
MobileSection.displayName = 'MobileSection';
